/**
 * API client for the admin backend.
 *
 * Handles the one genuinely tricky part of a 15-minute access token: when a
 * request comes back 401, refresh once and retry. Concurrent 401s share a single
 * in-flight refresh rather than each firing their own, otherwise a dashboard
 * loading eight panels would issue eight refreshes and invalidate itself.
 */

export const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  "http://localhost:3001/api/v1/admin";

const ROOT_API_BASE = API_BASE.replace(/\/admin\/?$/, "");

const ACCESS_KEY = "getpet-admin-access";
const REFRESH_KEY = "getpet-admin-refresh";
const PARTIAL_KEY = "getpet-admin-partial";

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  partial: () => sessionStorage.getItem(PARTIAL_KEY),

  setSession(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    sessionStorage.removeItem(PARTIAL_KEY);
  },
  setAccess(access: string) {
    localStorage.setItem(ACCESS_KEY, access);
  },
  setPartial(token: string) {
    sessionStorage.setItem(PARTIAL_KEY, token);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    sessionStorage.removeItem(PARTIAL_KEY);
  },
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Missing permission rather than a bad session. */
  get isForbidden() {
    return this.status === 403;
  }
  get isConflict() {
    return this.status === 409;
  }
  get isValidation() {
    return this.status === 422;
  }
  get isRateLimited() {
    return this.status === 429;
  }
}

interface ApiEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
}

type AuthMode = "access" | "partial" | "none";

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, unknown>;
  auth?: AuthMode;
  signal?: AbortSignal;
  /** Set for endpoints that return a file rather than the JSON envelope. */
  raw?: boolean;
}

const buildUrl = (path: string, query?: Record<string, unknown>): string => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  
  let targetUrl: string;
  if (
    cleanPath.startsWith("/auth") ||
    cleanPath === "/user/me" ||
    cleanPath.startsWith("/user/onboard") ||
    cleanPath.startsWith("/user/profile")
  ) {
    targetUrl = `${ROOT_API_BASE}${cleanPath}`;
  } else if (cleanPath.startsWith("/admin/")) {
    targetUrl = `${ROOT_API_BASE}${cleanPath}`;
  } else {
    targetUrl = `${API_BASE}${cleanPath}`;
  }

  const base = typeof window !== "undefined" ? window.location.origin : "http://localhost";
  const url = new URL(targetUrl, base);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      if (Array.isArray(v)) {
        for (const item of v) url.searchParams.append(k, String(item));
      } else {
        url.searchParams.set(k, String(v));
      }
    }
  }
  return url.toString();
};

// ── Refresh coordination ────────────────────────────────────────────────

let refreshInFlight: Promise<string | null> | null = null;
let onSessionLost: (() => void) | null = null;

/** Called by the auth store so a lost session can redirect to /login. */
export const setSessionLostHandler = (fn: () => void) => {
  onSessionLost = fn;
};

const doRefresh = async (): Promise<string | null> => {
  const refreshToken = tokens.refresh();
  if (!refreshToken) return null;

  try {
    const resp = await fetch(buildUrl("/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!resp.ok) return null;
    const json = (await resp.json()) as ApiEnvelope<{ accessToken: string }>;
    if (!json.data?.accessToken) return null;
    tokens.setAccess(json.data.accessToken);
    return json.data.accessToken;
  } catch {
    return null;
  }
};

const refreshOnce = (): Promise<string | null> => {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

// ── Core request ────────────────────────────────────────────────────────

const authHeader = (mode: AuthMode): Record<string, string> => {
  if (mode === "none") return {};
  const token = mode === "partial" ? tokens.partial() : tokens.access();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseError = async (resp: Response): Promise<ApiError> => {
  const requestId = resp.headers.get("X-Request-Id") ?? undefined;
  let message = `Request failed (${resp.status})`;
  try {
    const json = await resp.json();
    if (typeof json?.message === "string" && json.message) message = json.message;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(resp.status, message, requestId);
};

async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, query, auth = "access", signal, raw } = options;

  const send = (): Promise<Response> =>
    fetch(buildUrl(path, query), {
      method,
      signal,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...authHeader(auth),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let resp = await send();

  // One refresh-and-retry on an expired access token.
  if (resp.status === 401 && auth === "access" && tokens.refresh()) {
    const fresh = await refreshOnce();
    if (fresh) {
      resp = await send();
    } else {
      tokens.clear();
      onSessionLost?.();
      throw await parseError(resp);
    }
  }

  if (resp.status === 401 && auth === "access") {
    tokens.clear();
    onSessionLost?.();
  }

  if (!resp.ok) throw await parseError(resp);
  if (raw) return resp as unknown as T;

  const json = (await resp.json()) as ApiEnvelope<T>;
  return json.data;
}

/** Same as `request` but also returns the server's human-readable message. */
async function requestWithMessage<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; message: string }> {
  const { method = "GET", body, query, auth = "access", signal } = options;

  const send = (): Promise<Response> =>
    fetch(buildUrl(path, query), {
      method,
      signal,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...authHeader(auth),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

  let resp = await send();
  if (resp.status === 401 && auth === "access" && tokens.refresh()) {
    const fresh = await refreshOnce();
    if (fresh) resp = await send();
    else {
      tokens.clear();
      onSessionLost?.();
      throw await parseError(resp);
    }
  }
  if (!resp.ok) throw await parseError(resp);

  const json = (await resp.json()) as ApiEnvelope<T>;
  return { data: json.data, message: json.message };
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>(path, { query, signal }),

  post: <T>(path: string, body?: unknown, query?: Record<string, unknown>) =>
    request<T>(path, { method: "POST", body, query }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body }),

  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),

  /** Use where the server's message is worth surfacing in a toast. */
  postWithMessage: <T>(path: string, body?: unknown) =>
    requestWithMessage<T>(path, { method: "POST", body }),

  patchWithMessage: <T>(path: string, body?: unknown) =>
    requestWithMessage<T>(path, { method: "PATCH", body }),

  putWithMessage: <T>(path: string, body?: unknown) =>
    requestWithMessage<T>(path, { method: "PUT", body }),

  deleteWithMessage: <T>(path: string) =>
    requestWithMessage<T>(path, { method: "DELETE" }),

  /** Unauthenticated (login) and partial-token (MFA) calls. */
  public: <T>(path: string, body?: unknown) =>
    requestWithMessage<T>(path, { method: "POST", body, auth: "none" }),

  partial: <T>(path: string, body?: unknown) =>
    requestWithMessage<T>(path, { method: "POST", body, auth: "partial" }),

  /** Triggers a browser download for CSV/HTML endpoints. */
  async download(path: string, query?: Record<string, unknown>) {
    const resp = await fetch(buildUrl(path, query), {
      headers: authHeader("access"),
    });
    if (!resp.ok) throw await parseError(resp);

    const blob = await resp.blob();
    const disposition = resp.headers.get("Content-Disposition") ?? "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] ?? "getpet-export";

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ── Shared response shapes ──────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  nextCursor: string | null;
  total: number;
}
